import { useMemo } from 'react';
import {
  CachePolicies,
  Provider as FetchProvider,
  IncomingOptions,
} from 'use-http';

import { useAuthSelector, useAuthStore } from 'stores';
import { APIErrors, ICustomApiError } from 'types/error';
import {
  ICustomRetryOpts,
  IHttpProviderProps,
  IInterceptorReq,
} from 'types/httpClient';

const HttpProvider = ({
  children,
  baseUrl,
  customOptions,
}: IHttpProviderProps) => {
  const { handleLogout, remember, refreshTokens, tokens } = useAuthSelector();

  const globalOptions: IncomingOptions = {
    cachePolicy: CachePolicies.NO_CACHE,
    onError: ({ error }) => {
      throw error;
    },
    retries: 1,
    retryOn: async ({ error }: ICustomRetryOpts) => {
      if (!error || !remember) return false;
      if (
        'statusCode' in error &&
        error.statusCode === 401 &&
        error.key === APIErrors[APIErrors.ExpiredToken]
      ) {
        await refreshTokens();
        return true;
      }
      return false;
    },
    interceptors: {
      request: async ({ options }: IInterceptorReq): Promise<RequestInit> => {
        options.headers = [['Content-Type', 'application/json']];

        const authToken = useAuthStore.getState().tokens?.accessToken;
        if (authToken)
          options.headers = [
            ...options.headers,
            ['Authorization', `Bearer ${authToken}`],
          ];

        return options;
      },
      response: async ({ response }) => {
        if (!response.ok) {
          const statusCode = response.status;
          const errorData = response.data;
          const isUnauthorized = statusCode === 401;
          const isTokenStillValid =
            errorData.key !== APIErrors[APIErrors.ExpiredToken];
          const _isChangingPass = useAuthStore.getState().isChangingPassword;

          if (
            isUnauthorized &&
            !_isChangingPass &&
            (!remember || isTokenStillValid)
          )
            handleLogout();

          const customError: ICustomApiError = {
            statusCode,
            message: errorData.message || '',
            ...errorData,
          };

          throw customError;
        }
        return response;
      },
    },
  };

  const options: IncomingOptions = useMemo(
    () => ({
      ...globalOptions,
      ...customOptions,
    }),
    [customOptions, tokens]
  );

  return (
    <FetchProvider
      url={baseUrl || process.env.REACT_APP_HOST}
      options={options}
    >
      {children}
    </FetchProvider>
  );
};

export { HttpProvider };
